import { Box, Typography, Paper, Backdrop, CircularProgress, Grid } from '@mui/material';
import { API, graphqlOperation } from 'aws-amplify';
import { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { listAccountMasterv2, listaccounttransactiondetailstotals, listAccountTransactionDetailsv3 } from '../../../../graphql';
import { useSelector } from 'react-redux';
import { useSearchParams, useParams } from 'react-router-dom';
import { useReportContext } from '../ReportContext';
// import { defaultRowsPerPage } from '../../../../utils/common';
import LedgerNew from './ledgerNew';
const AllLedgers = () => {
  const dashboardData = useSelector((state) => state?.DashboardDataReducer);
  const { startTimeStamp, endTimeStamp } = dashboardData?.report;
  const { updateReportData, loading, setLoading, reportData } = useReportContext();
  const [searchParams] = useSearchParams();
  const { id: reportId } = useParams();
  const [tableData, setTableData] = useState([]);
  // const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage);
  const [dataCount, setDataCount] = useState(30);
  const previousReportIdRef = useRef(reportId);
  const previousIdsRef = useRef('');

  // Read includeOpeningBalance from URL, default to true if not present - memoized
  const includeOpeningBalance = useMemo(() => {
    const param = searchParams.get('includeOpeningBalance');
    return param !== null ? param === 'true' : true;
  }, [searchParams]);
  useEffect(() => {
    const apiCall = async () => {
      setLoading(true);

      const resAccounts = await API.graphql(graphqlOperation(listAccountMasterv2));
      // setData(resAccounts.data.listAccountMasterv2);

      // Pass data to Header component via context
      const combinedData = [...resAccounts.data.listAccountMasterv2];
      updateReportData({
        allAccounts: combinedData,
        loading: false,
      });

      setLoading(false);
    };
    apiCall();
  }, []);

  // Fetch transaction data for selected account IDs
  const idsParam = searchParams.get('ids');
  console.log(idsParam, 'idsParam');

  // Helper function to fetch account transactions
  const fetchAccountTransactions = useCallback(
    async (accountId) => {
      try {
        const [response, resCount] = await Promise.all([
          API.graphql(
            graphqlOperation(listAccountTransactionDetailsv3, {
              accountid: accountId,
              columnname: 'enteredtimestamp',
              searcharray: '[]',
              searchmode: 0,
              ordertype: 'asc',
              openingbalance: includeOpeningBalance ? 1 : 0,
              startdate: startTimeStamp,
              enddate: endTimeStamp,
              rowfrom: 0,
              limitno: 25,
            })
          ),
          API.graphql(
            graphqlOperation(listaccounttransactiondetailstotals, {
              accountid: accountId,
              startdate: startTimeStamp,
              openingbalance: includeOpeningBalance ? 1 : 0,
              enddate: endTimeStamp,
            })
          ),
        ]);

        return {
          accountid: accountId,
          data: response.data.listAccountTransactionDetailsv3,
          count: resCount.data.listaccounttransactiondetailstotals[0]?.transactioncount || 0,
          credit: resCount.data.listaccounttransactiondetailstotals[0]?.totalcredit || 0,
          debit: resCount.data.listaccounttransactiondetailstotals[0]?.totaldebit || 0,
          total: resCount.data.listaccounttransactiondetailstotals[0]?.closingbalance || 0,
        };
      } catch (err) {
        console.error(`Error fetching transactions for account ${accountId}:`, err);
        return null;
      }
    },
    [includeOpeningBalance, startTimeStamp, endTimeStamp]
  );

  // Helper function to update table data with fetched results
  const updateTableDataWithResults = useCallback((results, selectedIds) => {
    const newItems = results.map((result) => (result.status === 'fulfilled' ? result.value : null)).filter(Boolean);

    if (newItems.length > 0) {
      setTableData((prevItems) => {
        // Ensure we only keep items that are in the URL
        const filteredItems = prevItems.filter((item) => selectedIds.includes(String(item.accountid)));

        // Create a map for quick lookup
        const itemsMap = new Map(filteredItems.map((item) => [String(item.accountid), item]));

        // Add or update new items
        newItems.forEach((newItem) => {
          itemsMap.set(String(newItem.accountid), newItem);
        });

        // Return array maintaining order from selectedIds
        return selectedIds.map((id) => itemsMap.get(id)).filter(Boolean);
      });
    }
  }, []);

  // useEffect 1: Handle idsParam changes (only fetch for newly added IDs)
  useEffect(() => {
    previousReportIdRef.current = reportId;

    if (!idsParam) {
      // If no ids param, clear all data and no need to fetch
      setTableData([]);
      setLoading(false);
      previousIdsRef.current = '';
      return;
    }

    const decodedIds = decodeURIComponent(idsParam);
    const selectedIds = decodedIds
      .split(',')
      .filter(Boolean)
      .map((id) => id.trim());

    // Get previous IDs from ref
    const previousIds = previousIdsRef.current
      ? decodeURIComponent(previousIdsRef.current)
          .split(',')
          .filter(Boolean)
          .map((id) => id.trim())
      : [];

    // Find newly added IDs (in current list but not in previous list)
    const newlyAddedIds = selectedIds.filter((id) => !previousIds.includes(id));

    // Find removed IDs (in previous list but not in current list)
    const removedIds = previousIds.filter((id) => !selectedIds.includes(id));

    // Remove items that are not in the URL (no API call needed)
    if (removedIds.length > 0) {
      setTableData((prevItems) => {
        return prevItems.filter((item) => selectedIds.includes(String(item.accountid)));
      });
    }

    // Only fetch data for newly added IDs
    if (newlyAddedIds.length === 0) {
      // No new IDs to fetch, just update the ref
      previousIdsRef.current = idsParam;
      return;
    }

    // Show loader when fetching data for new IDs
    setLoading(true);

    // Fetch data only for newly added IDs
    const fetchPromises = newlyAddedIds.map((accountId) => fetchAccountTransactions(accountId));

    // Wait for all fetches to complete and batch update state once
    Promise.allSettled(fetchPromises).then((results) => {
      updateTableDataWithResults(results, selectedIds);
      setLoading(false);
      // Update the ref after fetching is complete
      previousIdsRef.current = idsParam;
    });
  }, [idsParam]);

  // useEffect 2: Handle filter changes (fetch all current IDs)
  useEffect(() => {
    if (!idsParam) {
      return;
    }

    const decodedIds = decodeURIComponent(idsParam);
    const selectedIds = decodedIds
      .split(',')
      .filter(Boolean)
      .map((id) => id.trim());

    if (selectedIds.length === 0) {
      return;
    }

    // Show loader when fetching data for all IDs
    setLoading(true);

    // Fetch data for all current IDs when filters change
    const fetchPromises = selectedIds.map((accountId) => fetchAccountTransactions(accountId));

    // Wait for all fetches to complete and batch update state once
    Promise.allSettled(fetchPromises).then((results) => {
      updateTableDataWithResults(results, selectedIds);
      setLoading(false);
    });
  }, [includeOpeningBalance, startTimeStamp, endTimeStamp]);

  // Memoize ledgersData to prevent unnecessary re-renders
  const ledgersData = useMemo(() => reportData?.allAccounts || [], [reportData?.allAccounts]);

  // Memoize setTableData callback to prevent child re-renders
  const handleSetTableData = useCallback((updater) => {
    setTableData(updater);
  }, []);

  // Memoize setdataCount callback
  const handleSetDataCount = useCallback((value) => {
    setDataCount(value);
  }, []);

  return (
    <>
      {tableData.map((data) => (
        <LedgerNew
          key={data.accountid}
          accountid={data.accountid}
          data={data.data}
          setTableData={handleSetTableData}
          includeOpeningBalance={includeOpeningBalance}
          count={data.count}
          ledgersData={ledgersData}
          setdataCount={handleSetDataCount}
          totalDebit={data.debit}
          totalCredit={data.credit}
          totalbalance={data.total}
        />
      ))}
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </>
  );
};

export default AllLedgers;
